import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AddApplicant } from '../../models/addapplicant';
import { ApplicationDataService } from '../../../services/application.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Property } from '../../models/property';
import { EventEmitterService } from 'services/event-emitter.service';
import { SplashService } from 'services/splash.service'; 
import { TelephoneNumber } from 'app/models/telephone-number';
import { ErrorService } from '../../../services/error.service';
import { AnalyticsService } from 'services/analytics.service';

const LoadingTimeout30Seconds = 30000;
@Component({
  selector: 'app-applicant',
  templateUrl: './applicant.component.html',
  styleUrls: ['./applicant.component.css']
})
export class ApplicantComponent implements OnInit {
  model: AddApplicant;
  errorMessage: string;
  displayInviteMsg = false;
  inviteExists = false;
  modelProperty: Property;
  splashHTML: string;
  homePhoneNumber: TelephoneNumber = new TelephoneNumber('','','');
  cellPhoneNumber: TelephoneNumber = new TelephoneNumber('','','');
  GuarantorNonLeaseHolderError: string;
  hasRentableItems: boolean;
  totalApplicationSteps: number = 8;
  @ViewChild('addApplicantForm') addApplicantForm: NgForm;
  isSubmitting: boolean = false;

  constructor(
    private applicationService: ApplicationDataService,
    public router: Router,
    public route: ActivatedRoute,
    private eventEmitterService: EventEmitterService,
    private splashService: SplashService,
    private errorService: ErrorService,
    private readonly analyticsService: AnalyticsService) {
      this.analyticsService.track(
        'HRA Application',
        'Applicant Information',
        'Start',
        'Start',
        '',
        this.additionalAnalyticsDetails());
    this.model = new AddApplicant();
    const RentalApplicationKey = localStorage.getItem('RentalApplicationKey');
    this.GuarantorNonLeaseHolderError = "Cannot Select Both Is Guarantor and Non Lease Holder. Please Choose One.";
    const correctStatus = localStorage.getItem('CorrectStatus');

    if (correctStatus != "true")
    {
      localStorage.removeItem('RentalApplicationKey');
      this.analyticsService.track(
        'HRA Application',
        'Applicant Information',
        'Stop',
        'Application not in the correct status',
        '/application/dashboard',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/dashboard']);
    }

    this.eventEmitterService.splashChangeEmitted$.subscribe(res => this.updateSplashHTML(res));
    this.splashService.updateSplash("apply");

    if (RentalApplicationKey !== undefined && RentalApplicationKey !== null) {
      this.model.RentalApplicationKey = Number(window.localStorage.getItem('RentalApplicationKey'));
    } else {
      this.analyticsService.track(
        'HRA Application',
        'Applicant Information',
        'Stop',
        'Application or applicant not in the right state',
        '/application/landing',
        this.additionalAnalyticsDetails());
      this.router.navigate(['/application/landing']);
    }
  }

  private additionalAnalyticsDetails(): any {
    try {
      return {
        'Property': this.modelProperty ?? {}
      };
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  updateSplashHTML(res: string)
  {
    this.splashHTML = res;
  }

  onNewApplicantClick() {
    // Prevent multiple submissions
    if (this.isSubmitting) {
      return;
    }
    
    // Validate form before proceeding
    if (!this.isFormValid()) {
      console.warn('Form validation failed.');
      return;
    }
    
    this.isSubmitting = true;

    this.model.HomePhone = this.homePhoneNumber.format();
    this.model.CellPhone = this.cellPhoneNumber.format();

    // Ensure boolean properties are explicitly set to prevent null values
    this.model.IsNonLeaseHolderOccupant = this.model.IsNonLeaseHolderOccupant || false;
    this.model.IsGuarantor = this.model.IsGuarantor || false;

    // Set a timeout to prevent stuck loading state
    const timeoutId : ReturnType<typeof setTimeout> = setTimeout(() => {
      this.isSubmitting = false;
      this.errorService.showError('Request timed out. Please try again.');
    }, LoadingTimeout30Seconds);
    
    if(this.model.IsGuarantor == true && this.model.IsNonLeaseHolderOccupant == true){
      clearTimeout(timeoutId);
      this.errorService.showError(this.GuarantorNonLeaseHolderError);
      this.isSubmitting = false;
    } else {
      this.applicationService.checkInviteRecord(this.model)
      .subscribe(
        res => {
          this.continueOnNewApplicantClick(res, timeoutId);
        }, 
        error => {
          clearTimeout(timeoutId);
          this.errorMessage = this.applicationService.handleError(error);
          this.isSubmitting = false;
        }
      );
    }
  }

  continueOnNewApplicantClick(res: boolean, timeoutId: ReturnType<typeof setTimeout>) {
    if(res == false)
    {
      this.applicationService.InviteApplicant(this.model)
      .subscribe(
        res => {
          this.clearForm();
          setTimeout(() => {
            clearTimeout(timeoutId);
            this.isSubmitting = false;
            this.router.navigate(['/application/applicant']);
          }, 500);
        },
        error => {
          clearTimeout(timeoutId);
          this.errorMessage = this.applicationService.handleError(error);
          this.isSubmitting = false;
        });
    }
    else {
      clearTimeout(timeoutId);
      this.isSubmitting = false;
      this.displayInviteMsg = true;
    }
  }

  /**
   * Clear all form data and reset to initial state
   */
  clearForm() {
    // Ensure model exists before clearing
    if (!this.model) {
      this.model = new AddApplicant();
    }
    
    this.model.FirstName = '';
    this.model.LastName = '';
    this.model.FullName = '';
    this.model.HomePhone = '';
    this.model.CellPhone = '';
    this.model.Email = '';
    this.model.IsNonLeaseHolderOccupant = false;
    this.model.IsGuarantor = false;
    
    // Reset phone numbers
    this.homePhoneNumber = new TelephoneNumber('', '', '');
    this.cellPhoneNumber = new TelephoneNumber('', '', '');
    
    // Reset form validation state
    if (this.addApplicantForm) {
      this.addApplicantForm.resetForm();
    }
    
    // Reset error messages
    this.errorMessage = '';
    this.displayInviteMsg = false;
    this.inviteExists = false;
  }

  onCloseInviteWarning() {
    this.displayInviteMsg = false;
  }

  onSubmit() {
    // Prevent multiple submissions
    if (this.isSubmitting) {
      return;
    }
    
    if (!this.isFormValid()) {
      console.warn('Form validation failed.');
      return;
    }
    
    this.isSubmitting = true;

    this.model.HomePhone = this.homePhoneNumber.format();
    this.model.CellPhone = this.cellPhoneNumber.format();

    // Ensure boolean properties are explicitly set to prevent null values
    this.model.IsNonLeaseHolderOccupant = this.model.IsNonLeaseHolderOccupant || false;
    this.model.IsGuarantor = this.model.IsGuarantor || false;

    // Set a timeout to prevent stuck loading state
    const timeoutId : ReturnType<typeof setTimeout> = setTimeout(() => {
      this.isSubmitting = false;
      this.errorService.showError('Request timed out. Please try again.');
    }, LoadingTimeout30Seconds);

    if(this.model.IsGuarantor == true && this.model.IsNonLeaseHolderOccupant == true){
      clearTimeout(timeoutId);
      this.errorService.showError(this.GuarantorNonLeaseHolderError);
      this.isSubmitting = false;
    }else{
      this.applicationService.checkInviteRecord(this.model)
      .subscribe(
        res => {
          this.continueOnSubmit(res, timeoutId);
        }, 
        res => {
          clearTimeout(timeoutId);
          this.errorMessage = this.applicationService.handleError(res);
          this.isSubmitting = false;
        }
      );
    }
  }

  resubmitInvite() {
    this.applicationService.reinviteApplicant(this.model)
      .subscribe(
        res => {
          this.analyticsService.track(
            'HRA Application',
            'Applicant Information',
            'Stop',
            'Reinviting applicant',
            '/application/applicants',
            this.additionalAnalyticsDetails());
          this.router.navigate(['/application/applicants'])
        },
        res => this.errorMessage = this.applicationService.handleError(res));
  }

  continueOnSubmit(res: boolean, timeoutId: ReturnType<typeof setTimeout>){
    if(res == false)
    {
      this.applicationService.InviteApplicant(this.model)
        .subscribe(
          res => {
            clearTimeout(timeoutId);
            this.isSubmitting = false;
            this.analyticsService.track(
              'HRA Application',
              'Applicant Information',
              'Stop',
              'Invite checked, sending to applicants',
              '/application/applicants',
              this.additionalAnalyticsDetails());
            this.router.navigate(['/application/applicants'])
          },
          res => {
            clearTimeout(timeoutId);
            this.errorMessage = this.applicationService.handleError(res);
            this.isSubmitting = false;
          });
    }
    else {
      clearTimeout(timeoutId);
      this.isSubmitting = false;
      this.displayInviteMsg = true;
    }
  }

  onBackClick() {
    this.model = null;
    window.history.back();
  }

  ngOnInit() {
    const rentableItems = localStorage.getItem('hasRentableItems');
    const verifastEnabled = localStorage.getItem('verifastEnabled');
    const hasAncillaryItems = localStorage.getItem('HasAncillaryItems');
    if ((rentableItems && rentableItems === 'true') || (hasAncillaryItems && hasAncillaryItems === 'true')) {
      this.hasRentableItems = true;
      this.totalApplicationSteps = 9;
    }

    if (verifastEnabled && verifastEnabled === 'true') {
      this.totalApplicationSteps++;
    }

    const property = JSON.parse(localStorage.getItem('SelectedProperty'));
    if (property != null) {
      this.modelProperty = property[0];
      if ((property[0].min_rent != null && property[0].max_rent != null) && (property[0].min_rent == property[0].max_rent)) {
        this.modelProperty.rent = property[0].min_rent.toLocaleString();
      }
      else if (property[0].min_rent != null && property[0].max_rent != null) {
        this.modelProperty.rent = property[0].min_rent.toLocaleString() + '-$' + property[0].max_rent.toLocaleString()
      } 
      else {
        this.modelProperty.rent = property[0].rent?.toLocaleString;
      }
    }
  }

  /**
   * Custom form validation method that properly checks phone number completeness
   */
  isFormValid(): boolean {
    if (this.isSubmitting) {
      return false;
    }

    // Check if basic form is valid
    const basicFormValid = this.addApplicantForm?.form?.valid;
    
    // Check if phone numbers are complete
    const homePhoneValid = this.isPhoneNumberComplete(this.homePhoneNumber);
    const cellPhoneValid = this.isPhoneNumberComplete(this.cellPhoneNumber);
    
    // Form is valid only if all validations pass
    return basicFormValid && homePhoneValid && cellPhoneValid;
  }

  /**
   * Check if a phone number object is complete (all parts filled)
   */
  isPhoneNumberComplete(phoneNumber: TelephoneNumber): boolean {
    return phoneNumber && 
           phoneNumber.area && 
           phoneNumber.exchange && 
           phoneNumber.subscriber &&
           phoneNumber.area.length === 3 &&
           phoneNumber.exchange.length === 3 &&
           phoneNumber.subscriber.length === 4;
  }
}
